/**
 * React Hook for Google Analytics 4 tracking
 */

import { useEffect, useCallback } from 'react';
import analytics from '../services/analytics';

/**
 * Custom hook for analytics tracking
 * @returns {Object} Analytics methods
 */
export function useAnalytics() {
  // Initialize analytics on mount
  useEffect(() => {
    analytics.initialize();
  }, []);

  // Waitlist tracking methods
  const trackWaitlistStart = useCallback((formPosition) => {
    analytics.trackWaitlistStart(formPosition);
  }, []);

  const trackWaitlistSubmit = useCallback((formPosition, email) => {
    analytics.trackWaitlistSubmit(formPosition, email);
  }, []);

  const trackWaitlistSuccess = useCallback((formPosition, result) => {
    analytics.trackWaitlistSuccess(formPosition, result);
  }, []);

  const trackWaitlistError = useCallback((formPosition, errorType, errorMessage) => {
    analytics.trackWaitlistError(formPosition, errorType, errorMessage);
  }, []);

  // Survey tracking methods
  const trackSurveyStart = useCallback((triggerSource, email) => {
    analytics.trackSurveyStart(triggerSource, email);
  }, []);

  const trackSurveyStepView = useCallback((stepNumber, stepName) => {
    analytics.trackSurveyStepView(stepNumber, stepName);
  }, []);

  const trackSurveyStepComplete = useCallback((stepNumber, stepName, answerCategory) => {
    analytics.trackSurveyStepComplete(stepNumber, stepName, answerCategory);
  }, []);

  const trackSurveyStepSkip = useCallback((stepNumber, stepName) => {
    analytics.trackSurveyStepSkip(stepNumber, stepName);
  }, []);

  const trackSurveyComplete = useCallback((stepsCompleted, stepsSkipped) => {
    analytics.trackSurveyComplete(stepsCompleted, stepsSkipped);
  }, []);

  const trackSurveyAbandon = useCallback((lastStep, stepsCompleted) => {
    analytics.trackSurveyAbandon(lastStep, stepsCompleted);
  }, []);

  // Interaction tracking methods
  const trackLocationSearch = useCallback((searchQuery, resultsCount, selectionMade) => {
    analytics.trackLocationSearch(searchQuery, resultsCount, selectionMade);
  }, []);

  const trackEventTypeSelect = useCallback((eventTypes, stepNumber) => {
    analytics.trackEventTypeSelect(eventTypes, stepNumber);
  }, []);

  const trackFrequencySelect = useCallback((frequencyValue, stepNumber) => {
    analytics.trackFrequencySelect(frequencyValue, stepNumber);
  }, []);

  const trackPainPointSelect = useCallback((painPoints, stepNumber) => {
    analytics.trackPainPointSelect(painPoints, stepNumber);
  }, []);

  const trackDeviceSelect = useCallback((deviceType, stepNumber) => {
    analytics.trackDeviceSelect(deviceType, stepNumber);
  }, []);

  // General tracking methods
  const trackEvent = useCallback((eventName, parameters) => {
    analytics.trackEvent(eventName, parameters);
  }, []);

  const setUserProperty = useCallback((property, value) => {
    analytics.setUserProperty(property, value);
  }, []);

  const setUserProperties = useCallback((properties) => {
    analytics.setUserProperties(properties);
  }, []);

  return {
    // Waitlist events
    trackWaitlistStart,
    trackWaitlistSubmit,
    trackWaitlistSuccess,
    trackWaitlistError,
    // Survey events
    trackSurveyStart,
    trackSurveyStepView,
    trackSurveyStepComplete,
    trackSurveyStepSkip,
    trackSurveyComplete,
    trackSurveyAbandon,
    // Interaction events
    trackLocationSearch,
    trackEventTypeSelect,
    trackFrequencySelect,
    trackPainPointSelect,
    trackDeviceSelect,
    // General methods
    trackEvent,
    setUserProperty,
    setUserProperties,
  };
}

export default useAnalytics;